import Vue from 'vue'
import Vuex from 'vuex'
import debounce from 'lodash/debounce'
import * as cst from '../constants'
import { findFile, round } from '../functions'
import stats from '../stats/stats'
import router from '../router/router'

Vue.use(Vuex)

function initialState() {
  return {
    recordDownloadURL: '',
    viewPortWidth: window.innerWidth,
    viewPortHeight: window.innerHeight,
    normalizedWidth: window.innerWidth,
    normalizedHeight: window.innerHeight,
    useNormalize: false,
    scaleFactor: 1,
    folderName: '',
    files: [],
    events: [],
  }
}

export default new Vuex.Store({
  strict: process.env.NODE_ENV === 'development',
  state: initialState(),
  mutations: {
    reset: state => {
      const s = initialState()
      Object.keys(s).forEach(key => {
        state[key] = s[key]
      })
    },
    setRecordDownloadURL(state, recordDownloadURL) {
      Vue.set(state, 'recordDownloadURL', recordDownloadURL)
    },
    setViewPortWidth: (state, viewPortWidth) => {
      Vue.set(state, 'viewPortWidth', viewPortWidth)
      // check if the normalized width has been updated already
      // if it isn't it's still using the view port width so we need to update it to the new view port height
      if (state.normalizedWidth === window.innerWidth) {
        Vue.set(state, 'normalizedWidth', viewPortWidth)
      }
    },
    setViewPortHeight: (state, viewPortHeight) => {
      Vue.set(state, 'viewPortHeight', viewPortHeight)
      // check if the normalized height has been updated already
      // if it isn't it's still using the view port height so we need to update it to the new view port height
      if (state.normalizedHeight === window.innerHeight) {
        Vue.set(state, 'normalizedHeight', viewPortHeight)
      }
    },
    setScaleFactor: (state, scaleFactor) => Vue.set(state, 'scaleFactor', scaleFactor),
    setNormalizedWidth: (state, normalizedWidth) => Vue.set(state, 'normalizedWidth', normalizedWidth),
    setNormalizedHeight: (state, normalizedHeight) => Vue.set(state, 'normalizedHeight', normalizedHeight),
    setUseNormalize: (state, useNormalize) => Vue.set(state, 'useNormalize', useNormalize),
    setFolderName: (state, folderName) => Vue.set(state, 'folderName', folderName),
    setFiles: (state, files) => Vue.set(state, 'files', files),
    setEvents: (state, events) => Vue.set(state, 'events', events),
  },
  getters: {
    relativeWidth(state) {
      return state.viewPortWidth * state.scaleFactor
    },
    relativeHeight(state) {
      // if the viewport height is the same as our window we can't just display it in full as it would push the timeline
      // and toolbar out of view, so we subtract their heights from the space we can use
      if (state.viewPortHeight === window.innerHeight) {
        return state.viewPortHeight * state.scaleFactor - (cst.TIMELINE_HEIGHT + cst.TOOLBAR_HEIGHT)
      }
      return state.viewPortHeight * state.scaleFactor
    },
    minTime(state) {
      return state.route.query.minTime
    },
    maxTime(state) {
      return state.route.query.maxTime
    },
    filteredEvents(state, getters) {
      const minTime = getters.minTime
      const maxTime = getters.maxTime
      return (state.events || []).filter(event => event.time >= minTime && event.time <= maxTime)
    },
    totalEvents(state, getters) {
      return (
        getters.filteredEvents
          // start, stop and end events do not count towards the total number of events, these all have a weight of 0
          .filter(event => event.weight > 0).length
      )
    },
    normalizedEvents(state, getters) {
      const filteredEvents = getters.filteredEvents
      if (!filteredEvents || !filteredEvents.length) {
        return []
      }

      // we set the first event's distance to 0 as this will be our anchor point, the beginning of the timeline
      const firstEvent = JSON.parse(JSON.stringify(filteredEvents[0]))
      firstEvent.distance = 0

      const [events, ,] = [firstEvent, ...filteredEvents.slice(1, filteredEvents.length)]
        // we don't use the STOP event for any data calculations
        .filter(event => event.event !== 'STOP')
        .reduce(
          ([eventsAcc, prevNormX, prevNormY], event) => {
            // the original event needs to be copied, VueX strict mode doesn't allow us to modify it
            const normalizedEvent = JSON.parse(JSON.stringify(event))

            const widthFactor = state.useNormalize ? state.normalizedWidth / state.viewPortWidth : 1
            const normalizedX = event.x * widthFactor
            const normalizedDX = prevNormX ? normalizedX - prevNormX : normalizedX

            const heightFactor = state.useNormalize ? state.normalizedHeight / state.viewPortHeight : 1
            const normalizedY = event.y * heightFactor
            const normalizedDY = prevNormY ? normalizedY - prevNormY : normalizedY

            // for MOUSE_WHEEL_END_SCROLL events the distance field contains mouse wheel detents we don't normalize those
            if (event.event !== 'MOUSE_WHEEL_END_SCROLL') {
              normalizedEvent.normalizedDistance = 0.0
              if (normalizedEvent.distance && normalizedEvent.distance > 0.0) {
                const a = normalizedDX * normalizedDX
                const b = normalizedDY * normalizedDY
                normalizedEvent.normalizedDistance = round(Math.sqrt(a + b))
              }
            }

            return [[...eventsAcc, normalizedEvent], normalizedX, normalizedY]
          },
          [[], 0.0, 0.0]
        )

      return events
    },
    activeEventNr(state) {
      return parseInt(state.route.params.eventNr)
    },
    activeEvent(state, getters) {
      return state.events[getters.activeEventNr]
    },
    totalTime(state, getters) {
      const events = getters.normalizedEvents
      return events && events.length ? events[events.length - 1].time - events[0].time : 0
    },
    totalDistance(state, getters) {
      return getters.normalizedEvents.reduce((acc, event) => {
        // for MOUSE_WHEEL_END_SCROLL events the distance field contains mouse wheel detents
        // we use the MOUSE_WHEEL_DISTANCE_FACTOR to transform these into 'mouse miles'
        if (event.event === 'MOUSE_WHEEL_END_SCROLL') {
          return acc + event.distance * cst.MOUSE_WHEEL_DISTANCE_FACTOR
        }
        return acc + event.normalizedDistance
      }, 0.0)
    },
    totalWeight(state, getters) {
      return getters.normalizedEvents.reduce((acc, event) => acc + event.weight, 0.0)
    },
    uxf(state, getters) {
      return ((getters.totalTime / 1000) * getters.totalDistance) / getters.totalWeight
    },
  },
  actions: {
    handleWindowResize: debounce(({ commit, state }) => {
      const maxHeight = window.innerHeight - (cst.TIMELINE_HEIGHT + cst.TOOLBAR_HEIGHT)
      const width = window.innerWidth
      const height = window.innerHeight > maxHeight ? maxHeight : window.innerHeight
      const scaleFactor = Math.min(width / state.viewPortWidth, height / state.viewPortHeight)
      commit('setScaleFactor', scaleFactor)
    }),
    handleFileUpload({ commit, dispatch }, uploadEvent) {
      commit('reset')

      stats.startSession()

      const files = uploadEvent.dataTransfer ? uploadEvent.dataTransfer.files : uploadEvent.target.files
      if (files && files.length) {
        commit('setFiles', files)

        const eventsFile = findFile(files, 'events.json')
        const fileReader = new FileReader()

        fileReader.onload = loadEvent => {
          const jsonContent = JSON.parse(loadEvent.target.result)

          commit('setFolderName', jsonContent.folder)

          commit('setViewPortWidth', jsonContent.screen_width)
          commit('setViewPortHeight', jsonContent.screen_height)

          const events = jsonContent.events
          events.forEach((event, index) => {
            event.key = '' + index
          })
          commit('setEvents', events)

          dispatch('handleWindowResize')

          router.push({
            name: 'event',
            params: { eventNr: 0 },
            query: { minTime: 0, maxTime: events[events.length - 1].time },
          })
        }

        fileReader.readAsText(eventsFile)
      }
    },
  },
})
